import { TickService } from 'app/shared/services/event-services';
import { Aurelia, autoinject } from 'aurelia-framework';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { Api } from 'app/shared/lib/api/api';
import { LogoutContext } from 'app/shared/lib/logout-context';
import { EssTenantSelectionModalCustomElement } from 'app/core/elements/ess-tenant-selection-modal';
import { Router, RouteConfig } from 'aurelia-router';
import { LocationContext } from 'app/shared/lib/location-context';
import { AppSession } from 'app/shared/lib/session/session';
import { ApiError } from 'app/shared/lib/api/api-types';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { LoginContext } from 'app/shared/lib/login-context';
import { AssistanceService } from 'app/shared/services/assistance-service';
import { IApiSessionLogin } from 'app/shared/lib/api/api-session-types';
import { isNullOrUndefined, sleep } from 'app/shared/lib/utilities';
import { EssDialogCustomElement } from 'app/shared/elements/ess-dialog';
import { State } from 'app/shared/lib/state';
import { Toaster } from 'app/shared/lib/toaster';
import { httpRequest } from 'app/shared/services/http-service';
import { EssUserAgreementModalCustomElement } from 'app/core/elements/ess-user-agreement-modal';
import { task } from 'app/shared/lib/decorators';
import { ISignOnProvider } from 'app/core/models/essette-interfaces';
import { MsalService } from 'app/shared/services/msal-service';
import { I18N } from 'aurelia-i18n';

const TIMER_INITIAL: number = -1;
const TIMER_DISABLED: number = -2;
const ATTEMPTS_UNTIL_RESET: number = 2;

let autoLogInDisabled: boolean = false;

@autoinject()
export class CheckinViewModel {

    protected systemMessage: object | null = null;

    public static disableAutoLogIn(): void {
        autoLogInDisabled = true;
    }

    protected loggingIn: boolean = false;
    protected errorMessage: string = '';
    protected passwordExpired: boolean = false;
    protected password: string = '';
    protected showLogoutContextMessage: boolean = false;
    protected timer: number = TIMER_INITIAL;
    protected attempts: number = 0;
    protected code: string | null = null;
    protected logins: IApiSessionLogin[] = [];

    protected signOnProviders?: ReadonlyArray<ISignOnProvider>;

    protected readonly state: State = new State();
    protected usernameString = '';
    protected passwordString = '';

    // refs
    protected $modal!: EssTenantSelectionModalCustomElement;
    protected $userAgreementModal!: EssUserAgreementModalCustomElement;
    protected $usernameSelectionModal!: EssDialogCustomElement;

    protected tick = () => {
        this.timer -= 1;
        if(this.timer === 0) {
            this.login();
        }
    };

    constructor(
        protected readonly app: Aurelia,
        protected readonly api: Api,
        protected readonly router: Router,
        protected readonly session: AppSession,
        protected readonly ticker: TickService,
        protected readonly service: AssistanceService,
        protected readonly logger: AppLogger,
        protected readonly env: EnvironmentService,
        protected readonly location: LocationContext,
        protected readonly loginContext: LoginContext,
        protected readonly logoutContext: LogoutContext,
        protected readonly msalService: MsalService,
        private readonly i18n: I18N,
        protected readonly toaster: Toaster) {
        this.showLogoutContextMessage = logoutContext.message == this.i18n.tr(
            'ui:messages.sessionExpired',
        );

        this.usernameString = this.i18n.tr('core:user.username.$').toLowerCase();
        this.passwordString = this.i18n.tr('core:user.password.$').toLowerCase();
    }

    protected async activate(params: any, config: RouteConfig) {
        try {
            this.state.beginLoad();

            // await system message (so if present, it's visible when the page displays)
            await this.loadSystemMessage();

            // INFO [Matt] if there's a code present, strip it off, save it, and replace the url
            const { code } = params;
            if(code !== undefined) {
                params.code = null;
                this.router.navigateToRoute(config.name!, params, { trigger: false, replace: true });
                if(code) {
                    this.code = code;
                }
                await this.retrieveLoginNames();
            }
            else if(this.loginContext.password) {
                this.password = this.loginContext.password;
                if(this.loginContext.loginName) {
                    // INFO [Matt] Awaiting this prevents the app from proceeding to login, which is a configured redirect on the app.
                    this.login();
                }
            }
            else {
                this.code = null;
                this.resetCredentials();
            }
        }
        catch(err) {
            if(err instanceof ApiError && err.hasError('request.failedAuthentication')) {
                this.errorMessage = this.i18n.tr('ui:messages.errors.failedAuthentication');
                this.toaster.error().message(this.errorMessage).show();
            }
            else {
                this.errorMessage = this.i18n.tr('ui:messages.errors.retrieveUserName');
                this.toaster.error().message({ key: 'ui:messages.errors.retrieveUserName', count: 1 }).show();
            }
        }
        finally {
            this.state.endLoad();
        }
    }

    protected deactivate() {
        this.errorMessage = '';
        this.passwordExpired = false;
    }

    protected bind(bindingContext: Object, overrideContext: Object): void {
        this.loadSignOnProviders();
    }

    protected attached(): void {
        this.enableTimer();
        if(this.logins?.length > 1) {
            this.showUsernameSelection();
        }
    }

    protected detached(): void {
        this.disableTimer();
    }

    protected enableTimer(): void {
        if(autoLogInDisabled) {
            this.timer = TIMER_DISABLED;
            return;
        }
        const autoLogInTimer = this.env.config.credentials.secondsToAutoLogIn;
        if(this.loginContext.loginName && this.password && this.timer === TIMER_INITIAL && autoLogInTimer > 0) {
            this.timer = autoLogInTimer;
            this.ticker.addEventListener('tick', this.tick);
        }
    }

    protected disableTimer(): void {
        this.timer = TIMER_DISABLED;
        this.ticker.removeEventListener('tick', this.tick);
        this.passwordExpired = false;
    }

    protected resetCredentials(): void {
        this.loginContext.resetCredentials();
        this.loginContext.loginName = this.env.config.credentials.loginName;
        this.password = this.env.config.credentials.password;
        this.attempts = 0;
    }

    @task
    private showUsernameSelection() {
        this.$usernameSelectionModal.open();
    }

    protected selectLoginName(loginName: string) {
        this.loginContext.loginName = loginName;
        this.$usernameSelectionModal.close();
    }

    private async loadSystemMessage(): Promise<void> {
        try {
            this.systemMessage = await httpRequest('system-message.json', { cache: 'none' });
        }
        catch {
            // ignore error
        }
    }

    /**
     * Load Sign On Providers
     */
    private async loadSignOnProviders(): Promise<void> {
        this.signOnProviders = this.env.config.signOnProviders?.filter(p => !p.hidden);
    }

    /**
     * Initiate the external Sign On process for a given Sign On Provider
     */
    private async externalSignOn(signOnProvider: ISignOnProvider): Promise<void> {
        if(!signOnProvider) { return; }
        this.router.navigateToRoute('signon', { provider: signOnProvider.key });
    }

    private async retrieveLoginNames(): Promise<void> {
        if(!this.code) { return; }
        try {
            const { logins } = await this.service.retrieveLoginNames(this.code);
            if(!logins || logins.length < 1) {
                throw Error();
            } else if(logins.length === 1) {
                this.loginContext.loginName = logins[0].loginName!;
            } else {
                this.logins = logins;
            }
        }
        catch(e) {
            if(e instanceof ApiError && e.hasError('request.failedAuthentication')) {
                this.errorMessage = this.i18n.tr('ui:messages.errors.failedAuthentication');
                this.toaster.error().message(this.errorMessage).show();
            }
        }
    }

    private async showPasswordWarning(expirationDate?: string): Promise<void> {
        // delay
        await sleep(1000);
        // show toast
        const result = await this.toaster
            .warn()
            .title({ key: 'ui:messages.password.warning' })
            .message({
                key: 'ui:messages.password.expiresSoon',
                replace: { when: expirationDate }
            })
            .action('navigate', {
                key: 'ui:messages.password.visitMyAccount'
            })
            .duration('indefinite')
            .icon('ui:icons.password')
            .show();
        if(result === 'navigate') {
            this.router.navigate('/core/my-account');
        }
    }

    protected async login(): Promise<void> {
        this.disableTimer();
        this.loggingIn = true;
        try {
            const result = await this.api.logIn(this.loginContext.loginName, this.password);
            const state = result.state || 'unknown';
            this.logoutContext.message = '';
            this.errorMessage = '';

            if(result.password?.state === 'expiresSoon') {
                this.showPasswordWarning(result.password.expirationDate || undefined); // do not await result
            }

            // if API says check in is available or required (or there's no tenant for some reason--as a backup)
            if(state === 'checkInAvailable' || state === 'checkInRequired' || !this.session.tenant) {
                const success = await this.$modal.open();
                if(!success) {
                    return void await this.api.logOut();
                }
            }
            // if there is a user agreement that needs to be accepted
            if(!isNullOrUndefined(this.env.config.agreementFile)) {
                const accepted = await this.$userAgreementModal.open();
                if(!accepted) {
                    return void await this.api.logOut();
                }
            }
            const body = document.querySelector('[aurelia-app]');
            const start = body!.getAttribute('start');

            // INFO [Matt] Navigate to either (1) the url the user was originally
            // trying to navigate to or otherwise (2) their configured home page.
            const landingPage = this.location.path || '/home';
            this.router.navigate(landingPage, { trigger: false, replace: true });
            await this.app.setRoot(start!);

            this.resetCredentials();
        }
        catch(err) {
            // Clear session after failed login attempt (will immediately log out other tabs)
            // This should also prevent errors with session info being persisted with invalid tokens
            // if the API changes token structure.
            this.session.clear('error');
            this.passwordExpired = false;

            if(err instanceof ApiError) {

                // TODO: use resource keys in ui.json
                if(err.hasError('request.failedAuthentication')) {
                    this.errorMessage = this.i18n.tr('core:local.login.authenticationFailed');
                }
                else if(err.hasError('password.expired')) {
                    this.errorMessage = this.i18n.tr('core:local.login.yourPasswordExpired');
                    this.passwordExpired = true;
                }
                else {
                    // general error message
                    this.errorMessage = this.i18n.tr('core:local.login.unableToAuthenticate');
                }

            }
            else {
                this.errorMessage = this.i18n.tr('core:local.login.unknownError');
            }

            // count attempts
            if(++this.attempts >= ATTEMPTS_UNTIL_RESET) {
                this.resetRememberedData();
            }
            this.logger.info(`${this.attempts} attempt(s)...`);

            throw err;
        }
        finally {
            this.loggingIn = false;
        }
    }

    protected resetRememberedData(): void {
        // clear remembered login data in case it's causing a problem
        this.logger.info('Resetting Remembered Login Data...');
        this.session.remember({ enabled: false });
    }

}
